import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { tutorSubmissionAPI, uploadAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Upload,
  Loader2,
  CheckCircle,
  User,
  BookOpen,
  MapPin,
  Clock,
  Award,
  X,
  Camera,
  FileText,
  Shield,
  FileCheck,
  ChevronRight,
  ChevronLeft,
  Send,
  Mail,
  UserCheck,
  ClipboardCheck,
  AlertCircle,
  XCircle
} from 'lucide-react';
import AvailabilitySelector from '../components/AvailabilitySelector';

// Document types for verification
const DOCUMENT_TYPES = [
  {
    key: 'government_id',
    title: 'Government-Issued ID',
    description: 'Valid passport, driver\'s license, or national ID card',
    required: true,
    icon: FileCheck
  },
  {
    key: 'proof_of_education',
    title: 'Proof of Education',
    description: 'Degree certificate, transcript, or enrollment verification',
    required: true,
    icon: GraduationCap
  },
  {
    key: 'police_check',
    title: 'Police Background Check',
    description: 'Recent police clearance certificate (within 6 months)',
    required: true,
    icon: Shield
  },
  {
    key: 'certification',
    title: 'Teaching Certifications',
    description: 'Any relevant teaching or tutoring certifications (optional)',
    required: false,
    icon: Award
  },
  {
    key: 'resume',
    title: 'Resume / CV',
    description: 'Your professional resume or curriculum vitae (optional)',
    required: false,
    icon: FileText
  }
];

const TutorSubmissionPage = () => {
  // Step management: 1 = Profile, 2 = Agreement, 3 = Documents, 4 = Complete
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile data
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    profile_photo_url: '',
    headline: '',
    subjects: [],
    grade_levels: [],
    university_courses: [],
    bio: '',
    qualifications: '',
    experience_years: 0,
    tutoring_mode: 'online',
    city: '',
    availability_summary: '',
    availability: {},
    languages: ['English']
  });

  // Agreement data
  const [agreementSections, setAgreementSections] = useState({});
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [acknowledgments, setAcknowledgments] = useState({});
  const [typedSignature, setTypedSignature] = useState('');
  
  // Documents data
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  
  // Submission tracking
  const [profileId, setProfileId] = useState(null);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const [newSubject, setNewSubject] = useState('');
  const [newGradeLevel, setNewGradeLevel] = useState('');
  const [newCourse, setNewCourse] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  const subjectOptions = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 
    'French', 'History', 'Geography', 'Economics', 'Computer Science',
    'Calculus', 'Statistics', 'Algebra', 'Geometry', 'Trigonometry'
  ];

  const gradeLevelOptions = [
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
    'University - Year 1', 'University - Year 2', 
    'University - Year 3', 'University - Year 4',
    'Graduate Level'
  ];

  // Load agreement content when reaching step 2
  useEffect(() => {
    if (currentStep === 2 && Object.keys(agreementSections).length === 0) {
      loadAgreement();
    }
  }, [currentStep]);

  const loadAgreement = async () => {
    setAgreementLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/agreement`);
      if (!response.ok) {
        throw new Error('Failed to load agreement');
      }
      const data = await response.json();
      setAgreementSections(data.sections || {});
      // Initialize acknowledgments
      const initialAck = {};
      Object.keys(data.sections || {}).forEach(key => {
        initialAck[key] = false;
      });
      setAcknowledgments(initialAck);
    } catch (error) {
      console.error('Error loading agreement:', error);
      toast.error('Failed to load agreement');
    } finally {
      setAgreementLoading(false);
    }
  };

  const handleAddItem = (field, value, setter) => {
    if (value.trim() && !formData[field].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const handleRemoveItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== value)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await uploadAPI.uploadFile(formDataUpload);
      const photoUrl = response.data.url;
      
      setFormData(prev => ({ ...prev, profile_photo_url: photoUrl }));
      setPhotoPreview(URL.createObjectURL(file));
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const validateStep1 = () => {
    if (!formData.full_name.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.headline.trim()) {
      toast.error('Please enter a professional headline');
      return false;
    }
    if (formData.subjects.length === 0) {
      toast.error('Please add at least one subject');
      return false;
    }
    if (!formData.bio.trim()) {
      toast.error('Please write a short bio');
      return false;
    }
    if (!formData.qualifications.trim()) {
      toast.error('Please enter your qualifications');
      return false;
    }
    return true;
  };

  const handleSubmitProfile = async () => {
    if (!validateStep1()) return;
    
    setLoading(true);
    try {
      const response = await tutorSubmissionAPI.submit(formData);
      setProfileId(response.data.profile_id || response.data.id);
      toast.success('Profile saved!');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const allAcknowledged = Object.values(acknowledgments).every(v => v === true);

  const handleSignAgreement = async () => {
    if (!allAcknowledged) {
      toast.error('Please acknowledge all sections of the agreement');
      return;
    }
    if (!typedSignature.trim()) {
      toast.error('Please type your full legal name as signature');
      return;
    }

    setLoading(true);
    try {
      // Send agreement signature with profile info (since tutor may not have account yet)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/agreement/sign-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          email: formData.email,
          name: formData.full_name,
          full_legal_name: typedSignature,
          acknowledgments
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to sign agreement');
      }
      
      const data = await response.json();
      setAgreementSigned(true);
      toast.success('Agreement signed successfully!');
      if (data.email_sent) {
        toast.success('Agreement copy sent to your email');
      }
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.message || 'Failed to sign agreement');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and PDF files are allowed');
      return;
    }

    setUploading(prev => ({ ...prev, [docType]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadResponse = await uploadAPI.uploadFile(formDataUpload);
      const fileUrl = uploadResponse.data.url;

      // Register document with profile
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/documents/upload-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          email: formData.email,
          document_type: docType,
          file_url: fileUrl,
          file_name: file.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to register document');
      }

      const data = await response.json();
      setDocuments(prev => ({
        ...prev,
        [docType]: { file_name: file.name, file_url: fileUrl, status: 'pending' }
      }));
      
      toast.success(`${DOCUMENT_TYPES.find(d => d.key === docType)?.title || docType} uploaded!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const requiredDocsUploaded = DOCUMENT_TYPES
    .filter(d => d.required)
    .every(d => documents[d.key]);

  const handleFinalSubmit = async () => {
    if (!requiredDocsUploaded) {
      toast.error('Please upload all required documents');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/submit-application-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          email: formData.email,
          name: formData.full_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit application');
      }

      const data = await response.json();
      setSubmissionComplete(true);
      toast.success('Application submitted successfully!');
      if (data.confirmation_email_sent) {
        toast.success('Confirmation email sent!');
      }
      setCurrentStep(4);
    } catch (error) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[
        { num: 1, label: 'Profile' },
        { num: 2, label: 'Agreement' },
        { num: 3, label: 'Documents' },
        { num: 4, label: 'Complete' }
      ].map((step, idx) => (
        <React.Fragment key={step.num}>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
            currentStep > step.num ? 'bg-green-100 text-green-700' :
            currentStep === step.num ? 'bg-indigo-100 text-indigo-700' :
            'bg-slate-100 text-slate-400'
          }`}>
            {currentStep > step.num ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs font-medium">
                {step.num}
              </span>
            )}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {idx < 3 && <div className="w-8 h-0.5 bg-slate-200"></div>}
        </React.Fragment>
      ))}
    </div>
  );

  // Completion state (Step 4)
  if (currentStep === 4 || submissionComplete) {
    return (
      <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="tutor-submission-complete">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <GraduationCap className="h-10 w-10 text-indigo-700" />
              <span className="font-heading text-2xl font-semibold text-slate-900">
                Gifted Academy Tutors
              </span>
            </Link>
          </div>

          <Card className="shadow-lg border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="pt-8 pb-10">
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-green-50">
                  <CheckCircle className="h-14 w-14 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Application Submitted!</h2>
                <p className="text-slate-600">Your tutor application is now under review.</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Profile Submitted</p>
                    <p className="text-sm text-slate-500">Your tutor profile information has been received</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Agreement Signed</p>
                    <p className="text-sm text-slate-500">Tutor agreement signed and copy sent to your email</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Upload className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Documents Uploaded</p>
                    <p className="text-sm text-slate-500">{Object.keys(documents).length} document(s) received for verification</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Confirmation Emails Sent</p>
                    <p className="text-sm text-slate-500">Agreement copy and confirmation sent to {formData.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Under Review</p>
                    <p className="text-sm text-slate-500">Our team will review your application within 2-5 business days</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 text-center">
                <h4 className="font-medium text-slate-800 mb-2">What Happens Next?</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Our team will carefully review your application and documents. 
                  You will receive an email notification once your application has been processed.
                </p>
                <p className="text-sm text-slate-500">
                  Questions? Contact us at <a href="mailto:contact@giftedacademytutors.ca" className="text-indigo-600 hover:underline">contact@giftedacademytutors.ca</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-indigo-700" />
            <span className="font-heading text-xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-6">
            <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900 mb-4">
              {currentStep === 1 && 'Join Our Tutoring Team'}
              {currentStep === 2 && 'Tutor Agreement'}
              {currentStep === 3 && 'Verification Documents'}
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              {currentStep === 1 && 'Share your expertise and help students achieve their academic goals.'}
              {currentStep === 2 && 'Please read and acknowledge each section of our tutor agreement.'}
              {currentStep === 3 && 'Upload required documents for identity and background verification.'}
            </p>
          </div>

          <StepIndicator />

          <AnimatePresence mode="wait">
            {/* Step 1: Profile */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <form onSubmit={(e) => { e.preventDefault(); handleSubmitProfile(); }} className="space-y-8">
                  {/* Basic Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5 text-indigo-600" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex flex-col items-center gap-4 p-6 bg-stone-50 rounded-xl">
                        <div className="relative">
                          <div className="w-28 h-28 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                            {photoPreview || formData.profile_photo_url ? (
                              <img 
                                src={photoPreview || formData.profile_photo_url} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="h-12 w-12 text-stone-400" />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-0 right-0 p-2 bg-indigo-600 rounded-full text-white shadow-lg hover:bg-indigo-700 transition-colors"
                            disabled={uploadingPhoto}
                          >
                            {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            className="hidden"
                            onChange={handlePhotoUpload}
                          />
                        </div>
                        <p className="text-sm text-slate-500">Upload a professional photo (optional)</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="full_name">Full Name *</Label>
                          <Input
                            id="full_name"
                            value={formData.full_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                            placeholder="Your full name"
                            required
                            data-testid="full-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="your@email.com"
                            required
                            data-testid="email-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="headline">Professional Headline *</Label>
                        <Input
                          id="headline"
                          value={formData.headline}
                          onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                          placeholder="e.g., Experienced Math Tutor | PhD in Mathematics"
                          required
                          data-testid="headline-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Teaching Profile */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-indigo-600" />
                        Teaching Profile
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="bio">Bio / About You *</Label>
                        <Textarea
                          id="bio"
                          value={formData.bio}
                          onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                          placeholder="Tell students about yourself, your teaching style, and what makes you a great tutor..."
                          rows={4}
                          required
                          data-testid="bio-textarea"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="qualifications">Qualifications & Education *</Label>
                        <Textarea
                          id="qualifications"
                          value={formData.qualifications}
                          onChange={(e) => setFormData(prev => ({ ...prev, qualifications: e.target.value }))}
                          placeholder="List your degrees, certifications, and relevant qualifications..."
                          rows={3}
                          required
                          data-testid="qualifications-textarea"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="experience">Years of Tutoring Experience</Label>
                        <Select 
                          value={String(formData.experience_years)} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, experience_years: parseInt(value) }))}
                        >
                          <SelectTrigger data-testid="experience-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                              <SelectItem key={num} value={String(num)}>
                                {num === 0 ? 'Less than 1 year' : num === 10 ? '10+ years' : `${num} year${num > 1 ? 's' : ''}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Subjects */}
                      <div className="space-y-2">
                        <Label>Subjects You Teach *</Label>
                        <div className="flex gap-2">
                          <Select value={newSubject} onValueChange={setNewSubject}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select a subject" />
                            </SelectTrigger>
                            <SelectContent>
                              {subjectOptions.filter(s => !formData.subjects.includes(s)).map(subject => (
                                <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" onClick={() => handleAddItem('subjects', newSubject, setNewSubject)}>
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.subjects.map(subject => (
                            <Badge key={subject} variant="secondary" className="gap-1">
                              {subject}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveItem('subjects', subject)} />
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Grade Levels */}
                      <div className="space-y-2">
                        <Label>Grade Levels</Label>
                        <div className="flex gap-2">
                          <Select value={newGradeLevel} onValueChange={setNewGradeLevel}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select grade level" />
                            </SelectTrigger>
                            <SelectContent>
                              {gradeLevelOptions.filter(g => !formData.grade_levels.includes(g)).map(grade => (
                                <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" onClick={() => handleAddItem('grade_levels', newGradeLevel, setNewGradeLevel)}>
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.grade_levels.map(grade => (
                            <Badge key={grade} variant="secondary" className="gap-1">
                              {grade}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveItem('grade_levels', grade)} />
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Languages */}
                      <div className="space-y-2">
                        <Label>Languages</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newLanguage}
                            onChange={(e) => setNewLanguage(e.target.value)}
                            placeholder="Add a language"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddItem('languages', newLanguage, setNewLanguage);
                              }
                            }}
                          />
                          <Button type="button" onClick={() => handleAddItem('languages', newLanguage, setNewLanguage)}>
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.languages.map(lang => (
                            <Badge key={lang} variant="secondary" className="gap-1">
                              {lang}
                              {formData.languages.length > 1 && (
                                <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveItem('languages', lang)} />
                              )}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Location & Availability */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-indigo-600" />
                        Location & Availability
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="tutoring_mode">Tutoring Mode</Label>
                          <Select 
                            value={formData.tutoring_mode} 
                            onValueChange={(value) => setFormData(prev => ({ ...prev, tutoring_mode: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="online">Online Only</SelectItem>
                              <SelectItem value="in-person">In-Person Only</SelectItem>
                              <SelectItem value="both">Both Online & In-Person</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">City / Location</Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                            placeholder="e.g., Toronto, ON"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="availability">Availability Summary</Label>
                        <Textarea
                          id="availability"
                          value={formData.availability_summary}
                          onChange={(e) => setFormData(prev => ({ ...prev, availability_summary: e.target.value }))}
                          placeholder="e.g., Available weekday evenings after 5pm and Saturday mornings..."
                          rows={2}
                        />
                      </div>

                      <div className="pt-4 border-t border-stone-200">
                        <AvailabilitySelector
                          availability={formData.availability}
                          onChange={(availability) => setFormData(prev => ({ ...prev, availability }))}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Next Button */}
                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="rounded-full px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                      data-testid="next-to-agreement-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          Continue to Agreement
                          <ChevronRight className="h-5 w-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 2: Agreement */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {agreementLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Agreement Sections */}
                    {Object.entries(agreementSections).map(([key, section]) => (
                      <Card key={key} className={`transition-all ${acknowledgments[key] ? 'border-green-200 bg-green-50/30' : ''}`}>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {acknowledgments[key] && <CheckCircle className="h-5 w-5 text-green-600" />}
                            {section.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-600 whitespace-pre-line text-sm mb-4">{section.content}</p>
                          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                            <Checkbox
                              id={`ack-${key}`}
                              checked={acknowledgments[key] || false}
                              onCheckedChange={(checked) => setAcknowledgments(prev => ({ ...prev, [key]: checked }))}
                            />
                            <label htmlFor={`ack-${key}`} className="text-sm font-medium cursor-pointer">
                              I have read and understand this section
                            </label>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {/* Signature */}
                    <Card className="border-indigo-200">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-indigo-600" />
                          Digital Signature
                        </CardTitle>
                        <CardDescription>
                          By typing your full legal name below, you agree to all terms and conditions.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="signature">Type your full legal name *</Label>
                            <Input
                              id="signature"
                              value={typedSignature}
                              onChange={(e) => setTypedSignature(e.target.value)}
                              placeholder="Your Full Legal Name"
                              className="text-lg font-medium"
                              data-testid="signature-input"
                            />
                          </div>
                          <p className="text-xs text-slate-500">
                            Acknowledged: {Object.values(acknowledgments).filter(Boolean).length} / {Object.keys(agreementSections).length} sections
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Navigation */}
                    <div className="flex justify-between pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCurrentStep(1)}
                        className="rounded-full px-6"
                      >
                        <ChevronLeft className="h-5 w-5 mr-2" />
                        Back to Profile
                      </Button>
                      <Button
                        onClick={handleSignAgreement}
                        disabled={loading || !allAcknowledged || !typedSignature.trim()}
                        className="rounded-full px-8 bg-indigo-600 hover:bg-indigo-700"
                        data-testid="sign-agreement-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            Signing...
                          </>
                        ) : (
                          <>
                            Sign & Continue
                            <ChevronRight className="h-5 w-5 ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Documents */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="space-y-6">
                  {/* Status Banner */}
                  <Card className={requiredDocsUploaded ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        {requiredDocsUploaded ? (
                          <>
                            <CheckCircle className="h-6 w-6 text-green-600" />
                            <div>
                              <p className="font-medium text-green-800">All Required Documents Uploaded</p>
                              <p className="text-sm text-green-700">You can now submit your application.</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-6 w-6 text-amber-600" />
                            <div>
                              <p className="font-medium text-amber-800">Documents Required</p>
                              <p className="text-sm text-amber-700">Please upload all required documents to proceed.</p>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Document Upload Cards */}
                  {DOCUMENT_TYPES.map((docType) => {
                    const uploaded = documents[docType.key];
                    const isUploading = uploading[docType.key];
                    const Icon = docType.icon;

                    return (
                      <Card 
                        key={docType.key}
                        className={`transition-all ${uploaded ? 'border-green-200 bg-green-50/30' : ''}`}
                        data-testid={`document-card-${docType.key}`}
                      >
                        <CardContent className="py-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                uploaded ? 'bg-green-100' : 'bg-slate-100'
                              }`}>
                                <Icon className={`h-6 w-6 ${uploaded ? 'text-green-600' : 'text-slate-600'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-900">{docType.title}</h3>
                                  {docType.required ? (
                                    <Badge variant="outline" className="text-xs">Required</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-slate-500">Optional</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 mb-2">{docType.description}</p>
                                
                                {uploaded && (
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-green-100 text-green-700">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Uploaded
                                    </Badge>
                                    <span className="text-xs text-slate-500">{uploaded.file_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex-shrink-0">
                              <input
                                type="file"
                                id={`file-${docType.key}`}
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp,.pdf"
                                onChange={(e) => handleDocumentUpload(docType.key, e.target.files[0])}
                                disabled={isUploading}
                              />
                              <Button
                                variant={uploaded ? "outline" : "default"}
                                size="sm"
                                onClick={() => document.getElementById(`file-${docType.key}`).click()}
                                disabled={isUploading}
                                data-testid={`upload-btn-${docType.key}`}
                              >
                                {isUploading ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    Uploading...
                                  </>
                                ) : uploaded ? (
                                  <>
                                    <Upload className="h-4 w-4 mr-1" />
                                    Replace
                                  </>
                                ) : (
                                  <>
                                    <Upload className="h-4 w-4 mr-1" />
                                    Upload
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {/* Document Guidelines */}
                  <Card className="bg-slate-50">
                    <CardContent className="py-4">
                      <h4 className="font-medium text-slate-800 mb-2">Document Guidelines</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Documents must be clear and readable</li>
                        <li>• Accepted formats: JPEG, PNG, WebP, PDF</li>
                        <li>• Maximum file size: 10MB per document</li>
                        <li>• Police background check must be issued within the last 6 months</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Navigation */}
                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep(2)}
                      className="rounded-full px-6"
                    >
                      <ChevronLeft className="h-5 w-5 mr-2" />
                      Back to Agreement
                    </Button>
                    <Button
                      onClick={handleFinalSubmit}
                      disabled={loading || !requiredDocsUploaded}
                      className="rounded-full px-8 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="submit-application-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Submit Application
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
};

export default TutorSubmissionPage;
